import {
  InstallationAddress,
  installationCreatePayloadSchema,
  InstallationLocalAddress,
  type InstallationCreatePayload
} from '@/features/installations/api/InstallationModel'
import { Container, LoadingScreen } from '@/shared/components'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  CardHeader,
  InputGroup,
  SelectListSingle,
  BreadcrumbsBar,
  useFieldLatinCharacterValidation,
  H2
} from '@/common'
import { useCallback, useMemo, useState } from 'react'
import { useSession } from '@/shared/hooks/useSession'
import useTranslatedZodErrors from '@/shared/hooks/useTranslatedZodErrors'
import { toast } from 'react-hot-toast'
import AddressCard from '@/components/AddressCard'
import {
  useInstallation,
  useUpdateInstallation
} from '@/features/installations/hooks/useInstallations'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import ReactCountryFlag from 'react-country-flag'
import LocalAddressCard from '@/components/LocalAddressCard'

function InstallationEditForm({
  initialValue,
  isUpdating,
  update
}: {
  initialValue: InstallationCreatePayload
  isUpdating: boolean
  update: (installation: InstallationCreatePayload) => Promise<void>
}) {
  const { t } = useTranslation()
  const { showToast } = useTranslatedZodErrors({
    stringKeysPrefix: 'installation.inputFields'
  })
  const {
    session: {
      organization: { placeOfRegistration: organizationPlaceOfRegistration }
    }
  } = useSession()

  const [installationState, setInstallationState] =
    useState<InstallationCreatePayload>(initialValue)

  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organizationPlaceOfRegistration
  )

  // Apply Latin character validation to the installation name
  const { error: nameError } = useFieldLatinCharacterValidation(
    installationState.name,
    t('common:errors.latinCharacters'),
    undefined
  )

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setInstallationState({ ...installationState, [evt.target.id]: evt.target.value })
    },
    [installationState]
  )

  const endMonth = useMemo(() => {
    return installationState.startMonth + installationState.monthInterval > 12
      ? (installationState.startMonth + installationState.monthInterval - 1) % 12
      : installationState.startMonth + installationState.monthInterval
  }, [installationState])

  const isNextEnabled = useMemo(() => {
    const result = installationCreatePayloadSchema.safeParse(installationState)
    return result.success
  }, [installationState])

  const onRestore = () => {
    setInstallationState(initialValue)
  }

  const onUpdate = async () => {
    const result = installationCreatePayloadSchema.safeParse(installationState)

    if (!result.success) {
      showToast(result.error.issues)
      return
    }

    await update(installationState)
    toast.success(t('installations:edit.notifications.updatedSuccessfully'))
  }
  const onAddressChange = useCallback(
    (payload: InstallationAddress) => {
      setInstallationState({
        ...installationState,
        ...payload,
        localStreet: payload.street,
        localStreetNumber: payload.streetNumber,
        localPostCode: payload.postCode,
        localCity: payload.city,
        localPoBox: payload.poBox,
        localStreet2: payload.street2,
        latitude: Number(payload.latitude),
        longitude: Number(payload.longitude)
      })
    },
    [installationState]
  )

  const onAddressWithLocalChange = useCallback(
    (payload: InstallationAddress & InstallationLocalAddress) => {
      setInstallationState({
        ...installationState,
        ...payload,
        latitude: Number(payload.latitude),
        longitude: Number(payload.longitude)
      })
    },
    [installationState]
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('installations:edit.sections.generalInformation')} />
        </CardHeader>
        {!doesOrganizationRequiresLocalAddress ? (
          <CardBody className="grid grid-cols-2 gap-4">
            <InputGroup
              id="name"
              label={t('installations:fields.name.label')}
              value={installationState.name}
              tooltip={t('installations:fields.name.tooltip')}
              onChange={onInputChange}
            />
            <InputGroup
              id="goodsFamily"
              label={t('installations:fields.goodsFamily.label')}
              value={t(`installations:fields.goodsFamily.options.${installationState.goodsFamily}`)}
              disabled
            />
          </CardBody>
        ) : (
          <CardBody className="grid grid-cols-3 gap-4">
            <InputGroup
              id="localName"
              label={
                <>
                  {t('installations:fields.localName.label')}
                  <ReactCountryFlag countryCode={organizationPlaceOfRegistration || ''} svg />
                </>
              }
              value={installationState.localName}
              tooltip={t('installations:fields.localName.tooltip')}
              onChange={onInputChange}
            />
            <InputGroup
              id="name"
              label={
                <>
                  {t('installations:fields.name.label')}
                  <ReactCountryFlag countryCode={'gb'} svg />
                </>
              }
              value={installationState.name}
              tooltip={t('installations:fields.name.tooltip')}
              onChange={onInputChange}
              error={nameError || undefined}
            />
            <InputGroup
              id="goodsFamily"
              label={t('installations:fields.goodsFamily.label')}
              value={t(`installations:fields.goodsFamily.options.${installationState.goodsFamily}`)}
              disabled
            />
          </CardBody>
        )}
      </Card>
      <Card>
        <CardHeader>
          <CardTitle title={t('installations:edit.sections.reportingPeriodBasis')} />
        </CardHeader>
        <CardBody className="space-y-4">
          {installationState.basisForReportingPeriod === 'CALENDAR_YEAR' && (
            <SelectListSingle<string>
              name="basisForReportingPeriod"
              items={[
                {
                  disabled: true,
                  label: t(
                    'installations:fields.basisForReportingPeriod.options.CALENDAR_YEAR.label'
                  ),
                  value: 'CALENDAR_YEAR',
                  id: 'CALENDAR_YEAR',
                  isChecked: true
                }
              ]}
              onChange={() => {}}
            />
          )}
          {installationState.basisForReportingPeriod === 'FISCAL_YEAR' && (
            <SelectListSingle<string>
              name="basisForReportingPeriod"
              items={[
                {
                  label: `${t(
                    'installations:fields.basisForReportingPeriod.options.FISCAL_YEAR.label'
                  )} (${t(`common:months.${installationState.startMonth}`)} - ${t(
                    `common:months.${endMonth}`
                  )})`,
                  disabled: true,
                  value: 'FISCAL_YEAR',
                  id: 'FISCAL_YEAR',
                  isChecked: true
                }
              ]}
              onChange={() => {}}
            />
          )}
          {installationState.basisForReportingPeriod ===
            'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME' && (
            <SelectListSingle<string>
              name="basisForReportingPeriod"
              items={[
                {
                  disabled: true,
                  label: `${t(
                    'installations:fields.basisForReportingPeriod.options.DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME.label'
                  )} (${t(`common:months.${installationState.startMonth}`)} - ${t(
                    `common:months.${endMonth}`
                  )})`,
                  value: 'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME',
                  id: 'DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME',
                  isChecked: true
                }
              ]}
              onChange={() => {}}
            />
          )}
        </CardBody>
      </Card>
      {!doesOrganizationRequiresLocalAddress ? (
        <AddressCard
          id="address"
          state={{
            street: installationState.street,
            streetNumber: installationState.streetNumber,
            street2: installationState.street2,
            postCode: installationState.postCode,
            city: installationState.city,
            poBox: installationState.poBox,
            country: installationState.country,
            unlocode: installationState.unlocode,
            latitude: installationState.latitude,
            longitude: installationState.longitude
          }}
          onChange={payload => onAddressChange(payload)}
        />
      ) : (
        <LocalAddressCard
          id="local-address"
          state={{
            street: installationState.street,
            streetNumber: installationState.streetNumber,
            street2: installationState.street2,
            postCode: installationState.postCode,
            city: installationState.city,
            poBox: installationState.poBox,
            country: installationState.country,
            unlocode: installationState.unlocode,
            latitude: installationState.latitude,
            longitude: installationState.longitude,
            localCity: installationState.localCity,
            localStreet: installationState.localStreet,
            localStreetNumber: installationState.localStreetNumber,
            localPostCode: installationState.localPostCode,
            localPoBox: installationState.localPoBox,
            localStreet2: installationState.localStreet2
          }}
          onChange={payload => onAddressWithLocalChange(payload)}
        />
      )}
      <footer className="flex justify-end gap-4">
        <Button variant="subtle" className="w-24" onClick={onRestore} disabled={isUpdating}>
          {t('common:actions.restore')}
        </Button>
        <Button
          className="w-24"
          isLoading={isUpdating}
          onClick={onUpdate}
          disabled={!isNextEnabled || isUpdating}
        >
          {t('common:actions.update')}
        </Button>
      </footer>
    </div>
  )
}

export default function InstallationEditPage() {
  const { t } = useTranslation()
  const installationId = useParams<{ installationId: string }>().installationId!
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()
  const {
    data: installationWithResources,
    isLoading,
    isSuccess
  } = useInstallation(organizationId, installationId)
  const { mutateAsync: updateInstallation, isPending: isUpdating } =
    useUpdateInstallation(installationId)

  const onInstallationUpdate = useCallback(
    async (installation: InstallationCreatePayload) => {
      await updateInstallation(installation)
    },
    [updateInstallation]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isSuccess) {
    return <Navigate to={`/dashboard`} />
  }

  return (
    <>
      <BreadcrumbsBar
        links={[
          { label: t('common:breadcrumbs.dashboard'), to: `/dashboard` },
          { label: installationWithResources.name, to: '' }
        ]}
      />
      <Container>
        <header className="flex justify-between items-center">
          <H2>{installationWithResources.name}</H2>
        </header>
        <InstallationEditForm
          initialValue={{
            name: installationWithResources.name,
            goodsFamily: installationWithResources.goodsFamily,
            street: installationWithResources.street,
            street2: installationWithResources.street2 || '',
            streetNumber: installationWithResources.streetNumber,
            country: installationWithResources.country,
            postCode: installationWithResources.postCode,
            basisForReportingPeriod: installationWithResources.basisForReportingPeriod,
            startMonth: installationWithResources.startMonth,
            monthInterval: installationWithResources.monthInterval,
            poBox: installationWithResources.poBox,
            city: installationWithResources.city,
            unlocode: installationWithResources.unlocode,
            longitude: installationWithResources.longitude,
            latitude: installationWithResources.latitude,
            localName: installationWithResources.localName,
            localCity: installationWithResources.localCity,
            localStreet: installationWithResources.localStreet,
            localStreetNumber: installationWithResources.localStreetNumber,
            localPostCode: installationWithResources.localPostCode,
            localStreet2: installationWithResources.localStreet2,
            localPoBox: installationWithResources.localPoBox
          }}
          isUpdating={isUpdating}
          update={onInstallationUpdate}
        />
      </Container>
    </>
  )
}
